/**
 * SurpriseMetricsCard Component
 *
 * Displays Titans "Learning by Surprise" metrics after draft generation.
 * Shows surprise score, confidence, explanation, and factor breakdown.
 */

import React from 'react';
import { Sparkles } from 'lucide-react';
import type { SurpriseMetrics } from '../../lib/surprise';

interface SurpriseMetricsCardProps {
    surprise: SurpriseMetrics;
}

export const SurpriseMetricsCard: React.FC<SurpriseMetricsCardProps> = ({ surprise }) => {
    const scoreColor =
        surprise.score >= 80 ? 'text-red-400' :
            surprise.score >= 60 ? 'text-orange-400' :
                surprise.score >= 40 ? 'text-yellow-400' :
                    'text-green-400';

    const scoreBarColor =
        surprise.score >= 80 ? 'bg-red-500' :
            surprise.score >= 60 ? 'bg-orange-500' :
                surprise.score >= 40 ? 'bg-yellow-500' :
                    'bg-green-500';

    return (
        <div className="p-4 rounded-xl bg-gradient-to-r from-purple-500/10 to-blue-500/10 border border-purple-500/30">
            {/* Header */}
            <div className="flex items-center gap-2 mb-4">
                <div className="p-1.5 rounded-lg bg-purple-500/20">
                    <Sparkles className="w-4 h-4 text-purple-400" />
                </div>
                <span className="text-sm font-semibold text-purple-400 uppercase tracking-wide">
                    Titans — Learning by Surprise
                </span>
            </div>

            {/* Score + Confidence */}
            <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                    <div className="text-xs text-hig-text-tertiary mb-1 uppercase tracking-wide">
                        Surprise Score
                    </div>
                    <div className="flex items-baseline gap-2">
                        <span className={`text-3xl font-bold ${scoreColor}`}>
                            {surprise.score}
                        </span>
                        <span className="text-sm text-hig-text-tertiary">/100</span>
                    </div>
                    {/* Score bar */}
                    <div className="mt-2 h-1.5 rounded-full bg-hig-bg-tertiary overflow-hidden">
                        <div
                            className={`h-full rounded-full ${scoreBarColor} transition-all duration-700`}
                            style={{ width: `${surprise.score}%` }}
                        />
                    </div>
                </div>
                <div>
                    <div className="text-xs text-hig-text-tertiary mb-1 uppercase tracking-wide">
                        Confidence
                    </div>
                    <div className="flex items-baseline gap-2">
                        <span className="text-3xl font-bold text-hig-accent">
                            {Math.round(surprise.confidence * 100)}%
                        </span>
                    </div>
                    <div className="mt-2 h-1.5 rounded-full bg-hig-bg-tertiary overflow-hidden">
                        <div
                            className="h-full rounded-full bg-hig-accent transition-all duration-700"
                            style={{ width: `${surprise.confidence * 100}%` }}
                        />
                    </div>
                </div>
            </div>

            {/* Explanations */}
            <div className="space-y-1.5 mb-4">
                {surprise.explanation.map((exp, i) => (
                    <div key={i} className="text-xs text-hig-text-secondary flex items-start gap-2">
                        <span className="text-purple-400 mt-0.5 flex-shrink-0">•</span>
                        <span>{exp}</span>
                    </div>
                ))}
            </div>

            {/* Factor breakdown */}
            <div className="grid grid-cols-4 gap-2">
                {([
                    { key: 'novelty', label: 'Novelty' },
                    { key: 'complexity', label: 'Complexity' },
                    { key: 'contextBreak', label: 'Context' },
                    { key: 'edgeCaseScore', label: 'Edge Case' },
                ] as const).map(({ key, label }) => (
                    <div key={key} className="text-center p-2 rounded-lg bg-hig-bg-secondary/50">
                        <div className="text-[10px] text-hig-text-tertiary uppercase tracking-wider mb-0.5">
                            {label}
                        </div>
                        <div className="text-sm font-semibold text-hig-text-primary">
                            {surprise.factors[key]}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default SurpriseMetricsCard;
